/**
 * SpaceDetail: instances of a space.
 *
 * Scoped to the CI/CD product:
 * - no queues (JMS queues), no pinned queue fill levels
 * - no monitor statistics (no "errors 24h" on cards)
 * - no "AI / permissions" tab, no "policies" tab
 * - no monitor options (time window, worker mode, high volume mode)
 * - no "open monitor", no "export access policies"
 *
 * What remains: create/edit/delete instances, connection data (OAuth plus
 * basic auth plus service key import), connection test, Werke, stage,
 * inheritance via serviceKeyVon, and the CI/CD binding hint.
 */
import { useState, useEffect, useCallback } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api } from "../utils/helpers";
import { useToast, ToastContainer } from "../components/ui";
import { TAG_ZU_STUFE, STAGES, stageLabel } from "../utils/stages";

// Icon set (same as SpacesHome)
const INST_ICONS = {
  layers:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
  server:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><circle cx="6" cy="6" r="1"/><circle cx="6" cy="18" r="1"/></svg>,
  database: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3"/></svg>,
  cloud:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/></svg>,
  code:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
  shield:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  bolt:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>,
  globe:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"/></svg>,
};

// Inline icons
const TestIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
const PencilIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const MoreIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>;
const SpinIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ animation: "iio-spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.22-8.56"/></svg>;
const CloseIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const PlusIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const BackIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;

// Which stage does the instance carry? The first tag that matches dev/qas/pre-prod/prod.
function stufeAusTags(tags) {
  for (const t of (Array.isArray(tags) ? tags : [])) {
    const k = String(t || "").trim().toLowerCase();
    const treffer = TAG_ZU_STUFE[k];
    if (treffer) return treffer;
  }
  return null;
}
// sachtags = everything that is not a stage. The stage has its own control.
function sachtagsVon(tags) {
  return (Array.isArray(tags) ? tags : []).filter(t => !TAG_ZU_STUFE[String(t || "").trim().toLowerCase()]);
}

// Feedback from the backend on whether credentials were pushed to a GitHub
// environment when saving. A silent push would be the worst possible
// feedback, so this lives as ONE function shared by create and edit.
// t is passed in because the function sits outside the component.
function cicdPushMeldung(res, t) {
  const c = res?.cicd;
  if (!c) return null;
  const felder = Array.isArray(c.felder) && c.felder.length ? c.felder.join(", ") : t("meldung.cicdFelder");
  const env = c.env || "?";
  if (c.gepusht) {
    return {
      text: c.repo
        ? t("meldung.cicdGepushtRepo", { env, repo: c.repo, felder })
        : t("meldung.cicdGepusht", { env, felder }),
      art: "success", dauer: 6500,
    };
  }
  return {
    text: t("meldung.cicdFehler", { grund: c.fehler || t("meldung.cicdGrundUnbekannt") }),
    art: "error", dauer: 9000,
  };
}

// Form template: no monitor fields (monitorHours/monitorWorkerMode/
// highVolumeMode), no AI capabilities and no access policies.
const EMPTY_FORM = {
  name: "", description: "", tags: ["", "", ""], stufe: "",
  tenantUrl: "", authType: "oauth",
  clientId: "", clientSecret: "", tokenUrl: "",
  username: "", password: "",
  werke: [],
  icon: "layers",
};

// CI/CD icons: shared with the sidebar and SpacesHome.
const CicdReadyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12h4l2-3 4 6 2-3h4"/><circle cx="12" cy="12" r="9"/>
  </svg>
);
const CicdOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9"/><path d="M12 8v4"/><path d="M12 16h.01"/>
  </svg>
);
const CicdWarnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const SpaceDetail = ({ space, cicdStatus, onBack, onSpaceUpdated }) => {
  const { t } = useTranslation("space");
  const { toasts, addToast } = useToast();
  const [instances, setInstances] = useState([]);
  const [openMenuFor, setOpenMenuFor] = useState(null);
  const sRole = space?.role || "owner";
  const isOwnerSpace = !space?.isShared && sRole === "owner";
  const canManage = isOwnerSpace || sRole === "admin";

  // Modal state
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalTab, setModalTab] = useState("connection");
  const [form, setForm] = useState(EMPTY_FORM);
  const [delConfirm, setDelConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [testingInModal, setTestingInModal] = useState(false);
  // Action result modal: shows the output of instance actions
  const [actionResult, setActionResult] = useState(null);
  const [busyAction, setBusyAction] = useState({});

  const load = useCallback(async () => {
    if (!space?.id) return;
    try {
      const insts = await api(`/api/spaces/${space.id}/instances`);
      setInstances(insts);
    } catch {}
  }, [space?.id]);

  useEffect(() => { load(); }, [load]);

  // Esc closes any open dialog or menu
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showCreate || editing) closeModal();
      else if (openMenuFor) setOpenMenuFor(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // A click outside closes the more menu
  useEffect(() => {
    if (!openMenuFor) return;
    const onClick = (e) => {
      if (!e.target.closest?.(".iio-card-menu, [data-more-trigger]")) setOpenMenuFor(null);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [openMenuFor]);

  // Form handlers
  const istGeerbt = !!form.serviceKeyVon;
  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setTag = (idx, value) => setForm(prev => {
    const tags = [...(prev.tags || ["", "", ""])];
    tags[idx] = value;
    return { ...prev, tags };
  });

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setModalTab("json");
    setShowCreate(true);
  };

  const openEdit = (inst) => {
    setForm({
      ...EMPTY_FORM,
      ...inst,
      description: inst.description || "",
      stufe: stufeAusTags(inst.tags) || "",
      serviceKeyVon: inst.serviceKeyVon || null,
      vorlageName: inst.vorlageName || null,
      tags: [...sachtagsVon(inst.tags), "", "", "", ""].slice(0, 4),
      // Do not pre-fill secrets: empty means "unchanged"
      clientSecret: "",
      password: "",
    });
    setEditing(inst);
    setModalTab("connection");
    setDelConfirm("");
    setOpenMenuFor(null);
  };
  const closeModal = () => {
    setShowCreate(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setDelConfirm("");
    setSubmitting(false);
    setTestingInModal(false);
  };

  const submitForm = async () => {
    if (!form.name.trim() || !form.tenantUrl.trim()) {
      addToast(t("meldung.pflichtfelder"), "error");
      return;
    }
    setSubmitting(true);
    try {
      // Merge sachtags and stage back into ONE list. The loop variable is
      // named tag, not t, or it would shadow the translation function.
      const sach = (form.tags || []).map(tag => (tag || "").trim()).filter(Boolean).filter(tag => !TAG_ZU_STUFE[tag.toLowerCase()]).slice(0, 4);
      const cleanTags = form.stufe ? [...sach, form.stufe] : sach;
      const payload = { ...form, tags: cleanTags };
      // Trim the username (whitespace from copy/paste), but not passwords
      if (payload.clientId) payload.clientId = String(payload.clientId).trim();
      // When editing: do not send empty secrets (means "unchanged")
      if (editing) {
        if (!payload.clientSecret) delete payload.clientSecret;
        if (!payload.password) delete payload.password;
      }
      if (editing) {
        const res = await api(`/api/instances/${editing.id}`, { method: "PUT", body: JSON.stringify(payload) });
        addToast(t("meldung.instanzAktualisiert"), "success");
        { const m = cicdPushMeldung(res, t); if (m) addToast(m.text, m.art, m.dauer); }
      } else {
        const res = await api(`/api/spaces/${space.id}/instances`, { method: "POST", body: JSON.stringify(payload) });
        addToast(t("meldung.instanzErstellt"), "success");
        { const m = cicdPushMeldung(res, t); if (m) addToast(m.text, m.art, m.dauer); }
      }
      closeModal();
      load();
    } catch (e) {
      addToast(e.message, "error");
    }
    setSubmitting(false);
  };

  const submitDelete = async () => {
    if (!editing || delConfirm !== editing.name) return;
    try {
      await api(`/api/instances/${editing.id}`, { method: "DELETE" });
      addToast(t("meldung.instanzGeloescht"), "success");
      closeModal();
      load();
    } catch (e) {
      addToast(e.message, "error");
    }
  };

  // Card actions
  const setBusy = (instId, type) => setBusyAction(prev => type
    ? { ...prev, [instId]: type }
    : (() => { const n = { ...prev }; delete n[instId]; return n; })());

  const runInstanceAction = async ({ inst, type, label, method, url, body, transform }) => {
    setBusy(inst.id, type);
    setActionResult({ type, instId: inst.id, instName: inst.name, label, loading: true, data: null, error: null });
    try {
      const opts = { method: method || "GET" };
      if (body) opts.body = JSON.stringify(body);
      const r = await api(url, opts);
      const data = transform ? transform(r) : r;
      setActionResult({ type, instId: inst.id, instName: inst.name, label, loading: false, data, error: null });
    } catch (e) {
      setActionResult({ type, instId: inst.id, instName: inst.name, label, loading: false, data: null, error: e.message || t("gemeinsam:zustand.fehler") });
    }
    setBusy(inst.id, null);
  };

  const testConnection = (inst) => runInstanceAction({
    inst, type: "test", label: t("aktion.test"),
    method: "POST", url: `/api/instances/${inst.id}/test`,
  });
  const closeActionResult = () => setActionResult(null);

  // Render
  return (
    <>
      <ToastContainer toasts={toasts}/>

      {/* Header */}
      <div className="iio-page-head iio-anim-glide">
        <div className="iio-page-head-left iio-anim-arrival" style={{ animationDelay: "60ms" }}>
          <div className="iio-breadcrumb">
            <button type="button" onClick={onBack} style={{ background: "transparent", border: 0, padding: 0, font: "inherit", color: "var(--iio-text-3)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <BackIcon/><span>{t("kopf.brotkrume")}</span>
            </button>
            <span className="iio-sep">/</span>
            <button type="button" onClick={onBack} style={{ background: "transparent", border: 0, padding: 0, font: "inherit", color: "var(--iio-text-3)", cursor: "pointer", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "var(--iio-font-mono)" }}>{t("kopf.brotkrumeSpaces")}</button>
            <span className="iio-sep">/</span>
            <span className="iio-accent">{space.name}</span>
          </div>
          <h1>{space.name}.</h1>
          <p className="iio-lead">
            {space.description || t("kopf.leadLeer")}
          </p>
          {(() => {
            const c = cicdStatus?.spaces?.[space?.id];
            if (!c) return null;
            const stateReady = !!c.pipelineReady;
            const stateOff = c.configured && !stateReady;
            const cls = stateReady ? "iio-space-cicd is-ready"
                       : stateOff ? "iio-space-cicd is-off"
                       : "iio-space-cicd";
            return (
              <div className={cls}>
                {stateReady ? <CicdReadyIcon/> : <CicdOffIcon/>}
                <span className="txt">
                  {stateReady
                    ? t("kopf.cicdBereit")
                    : t("kopf.cicdOhneGithub")}
                </span>
                {c.repo && (
                  <span className="sub">{t("kopf.cicdRepo", { repo: c.repo })}</span>
                )}
                {c.geteiltMit > 0 && (
                  <span className="warn" role="alert">
                    <CicdWarnIcon/> {t("kopf.cicdGeteilt", { count: c.geteiltMit + 1 })}
                  </span>
                )}
              </div>
            );
          })()}
        </div>
        {isOwnerSpace && (
          <div className="iio-page-head-right iio-anim-arrival" style={{ animationDelay: "120ms" }}>
            <button className="iio-btn iio-btn--secondary" onClick={() => onSpaceUpdated && onSpaceUpdated({})}>{t("kopf.spaceBearbeiten")}</button>
            <button className="iio-btn iio-btn--signal" onClick={openCreate}>
              <PlusIcon/>{t("kopf.neueInstanz")}
            </button>
          </div>
        )}
      </div>

      {/* Card grid */}
      <div className="iio-grid iio-anim-glide">
        {instances.map((inst, cardIdx) => {
          const tagsToShow = (inst.tags || []).slice(0, 4);
          const menuOpen = openMenuFor === inst.id;
          return (
            <article
              key={inst.id}
              className="iio-space-card iio-anim-stagger"
              style={{ animationDelay: `${160 + Math.min(cardIdx, 8) * 40}ms` }}
              tabIndex={0}
              onClick={() => { if (canManage) openEdit(inst); }}
              onKeyDown={(e) => { if (e.key === "Enter" && canManage) openEdit(inst); }}
            >
              {canManage && (<div className="iio-card-actions" onClick={(e) => e.stopPropagation()}>
                <button className="iio-card-action" title={t("karte.aktionTest")} onClick={() => testConnection(inst)} disabled={busyAction[inst.id] === "test"}>
                  {busyAction[inst.id] === "test" ? <SpinIcon/> : <TestIcon/>}
                </button>
                <button className="iio-card-action" title={t("gemeinsam:aktion.bearbeiten")} onClick={() => openEdit(inst)}><PencilIcon/></button>
                {isOwnerSpace && (
                  <div style={{ position: "relative" }}>
                    <button className="iio-card-action" title={t("karte.aktionMehr")} data-more-trigger onClick={() => setOpenMenuFor(menuOpen ? null : inst.id)}><MoreIcon/></button>
                    {menuOpen && (
                      <div className="iio-card-menu" onClick={(e) => e.stopPropagation()} style={{
                        position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 10,
                        background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)",
                        borderRadius: "var(--iio-r-md)",
                        boxShadow: "var(--iio-shadow-md)",
                        minWidth: 200, padding: 4,
                      }}>
                        <button
                          onClick={async () => {
                            setOpenMenuFor(null);
                            if (!window.confirm(t("karte.loeschenFrage", { name: inst.name }))) return;
                            try {
                              await api(`/api/instances/${inst.id}`, { method: "DELETE" });
                              addToast(t("meldung.instanzGeloescht"), "success");
                              load();
                            } catch (e) { addToast(e.message, "error"); }
                          }}
                          style={{
                            display: "block", width: "100%", textAlign: "left",
                            padding: "8px 12px", background: "transparent", border: 0,
                            font: "inherit", fontSize: 13, color: "var(--iio-critical)",
                            cursor: "pointer", borderRadius: "var(--iio-r-sm)",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--iio-bg-3)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          {t("karte.menuLoeschen")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>)}

              <div className="iio-card-head">
                <div className="iio-card-icon">{INST_ICONS[inst.icon] || INST_ICONS.layers}</div>
                <div className="iio-card-title">
                  <h3>{inst.name}</h3>
                  {tagsToShow.length > 0 && (
                    <div className="iio-meta iio-card-tags">
                      {/* Loop variable tag, not t, or it would shadow the translation function. */}
                      {tagsToShow.map((tag, i) => <span key={i} className="iio-card-tag">{tag}</span>)}
                    </div>
                  )}
                </div>
              </div>

              <div className={`iio-card-desc${!inst.description ? " iio-card-desc--empty" : ""}`}>
                {inst.description || t("karte.keineBeschreibung")}
              </div>

              {inst.credsIncomplete && (
                <div style={{ fontSize: 12, color: "var(--iio-warning)", fontWeight: 600, margin: "2px 0 6px" }}>
                  {t("karte.credsUnvollstaendig")}
                </div>
              )}

            </article>
          );
        })}

        {/* New instance (owner only) */}
        {isOwnerSpace && (
          <article
            className="iio-space-card iio-space-card--new iio-anim-stagger"
            style={{ animationDelay: `${200 + Math.min(instances.length, 8) * 40}ms` }}
            tabIndex={0}
            onClick={openCreate}
            onKeyDown={(e) => { if (e.key === "Enter") openCreate(); }}
          >
            <div className="iio-plus">+</div>
            <div className="iio-lbl">{t("karte.neuTitel")}</div>
            <div className="iio-sub">{t("karte.neuSub")}</div>
          </article>
        )}
      </div>

      {/* Action result modal */}
      {actionResult && (
        <div className="iio-modal-overlay is-open" onClick={(e) => { if (e.target === e.currentTarget) closeActionResult(); }}>
          <div className="iio-modal" role="dialog" aria-modal="true" style={{ maxWidth: 720 }}>
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{actionResult.label}</span>
                <h2>{actionResult.instName}</h2>
              </div>
              <button className="iio-modal-close" onClick={closeActionResult}><CloseIcon/></button>
            </header>

            <div className="iio-modal-body">
              {actionResult.loading && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, color: "var(--iio-text-2)", fontSize: 14, padding: "16px 0" }}>
                  <div style={{ width: 16, height: 16, color: "var(--iio-brand-ink)" }}><SpinIcon/></div>
                  <span>{t("aktion.laeuft")}</span>
                </div>
              )}

              {!actionResult.loading && actionResult.error && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", border: "1px solid var(--iio-critical)", borderLeft: "3px solid var(--iio-critical)", borderRadius: "var(--iio-r-sm)", color: "var(--iio-critical)", fontSize: 13 }}>
                  <span style={{ width: 2, height: 13, background: "currentColor" }}/>
                  {actionResult.error}
                </div>
              )}

              {!actionResult.loading && !actionResult.error && actionResult.data && (
                <>
                  {actionResult.type === "test" && (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", border: `1px solid ${actionResult.data.success ? "var(--iio-success)" : "var(--iio-critical)"}`, borderLeft: `3px solid ${actionResult.data.success ? "var(--iio-success)" : "var(--iio-critical)"}`, borderRadius: "var(--iio-r-sm)", color: actionResult.data.success ? "var(--iio-success)" : "var(--iio-critical)", fontSize: 14, fontWeight: 600 }}>
                        <span style={{ width: 2, height: 14, background: "currentColor" }}/>
                        {actionResult.data.success ? t("aktion.testErfolg") : (actionResult.data.message || t("aktion.testFehler"))}
                      </div>
                      {actionResult.data.tokenObtained && <div style={{ marginTop: 12, fontSize: 13, color: "var(--iio-text-2)" }}>{t("aktion.tokenOk")}</div>}
                      {actionResult.data.tenantInfo && (
                        <div style={{ marginTop: 12, padding: 12, background: "var(--iio-bg-1)", borderRadius: "var(--iio-r-sm)", fontFamily: "var(--iio-font-mono)", fontSize: 12, color: "var(--iio-text-2)" }}>
                          {Object.entries(actionResult.data.tenantInfo).map(([k, val]) => (
                            <div key={k} style={{ marginBottom: 4 }}><span style={{ color: "var(--iio-text-3)" }}>{k}:</span> {String(val)}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                </>
              )}
            </div>

            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left"/>
              <div className="iio-modal-foot-right">
                <button className="iio-btn iio-btn--secondary" onClick={closeActionResult}>{t("gemeinsam:aktion.schliessen")}</button>
              </div>
            </footer>
          </div>
        </div>
      )}

      {/* Create / edit modal */}
      {(showCreate || editing) && (
        <div className="iio-modal-overlay is-open" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="iio-modal" role="dialog" aria-modal="true" style={{ maxWidth: 640 }}>
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{editing ? t("dialog.augenbraueBearbeiten") : t("dialog.augenbraueAnlegen")}</span>
                <h2>{editing ? t("dialog.titelBearbeiten", { name: editing.name }) : t("dialog.titelNeu")}</h2>
              </div>
              <button className="iio-modal-close" onClick={closeModal}><CloseIcon/></button>
            </header>

            <div className="iio-modal-tabs">
              {/* Loop variable reiter, not t, or it would shadow the translation function. */}
              {[
                ...(!editing ? [{ key: "json", lbl: t("dialog.tabJson") }] : []),
                { key: "connection", lbl: t("dialog.tabVerbindung") },
                { key: "werke", lbl: t("dialog.tabWerke") },
              ].map(reiter => (
                <button key={reiter.key} type="button" className={`iio-modal-tab${modalTab === reiter.key ? " is-active" : ""}`} onClick={() => setModalTab(reiter.key)}>
                  {reiter.lbl}
                </button>
              ))}
            </div>

            <div className="iio-modal-body">
              {modalTab === "json" && !editing && <>
                <div className="iio-field">
                  <span className="iio-field-label">{t("json.titel")}</span>
                  <div style={{ fontSize: 12, color: "var(--iio-text-2)", marginBottom: 10, lineHeight: 1.5 }}>
                    {t("json.info")}
                  </div>
                  <textarea className="iio-textarea" style={{ minHeight: 180, fontSize: 12, lineHeight: 1.6 }}
                    placeholder={'{\n  "oauth": {\n    "clientid": "sb-...",\n    "clientsecret": "...",\n    "url": "https://...",\n    "tokenurl": "https://..."\n  }\n}'}
                    onPaste={e => {
                      const text = e.clipboardData.getData("text");
                      try {
                        const parsed = JSON.parse(text);
                        const o = parsed.oauth || parsed;
                        if (o.url) setField("tenantUrl", String(o.url).replace(/\/+$/, ""));
                        if (o.clientid || o.clientId) setField("clientId", o.clientid || o.clientId);
                        if (o.clientsecret || o.clientSecret) setField("clientSecret", o.clientsecret || o.clientSecret);
                        if (o.tokenurl || o.tokenUrl) setField("tokenUrl", o.tokenurl || o.tokenUrl);
                        setField("authType", "oauth");
                        addToast(t("meldung.serviceKeyImportiert"), "success");
                        setModalTab("connection");
                        e.preventDefault();
                      } catch {
                        addToast(t("meldung.serviceKeyUngueltig"), "error");
                      }
                    }}/>
                </div>
              </>}

              {modalTab === "connection" && <>
                <div className="iio-field">
                  <label className="iio-field-label" htmlFor="iio-inst-name">{t("verbindung.name")}</label>
                  <input id="iio-inst-name" className="iio-input" autoFocus value={form.name} onChange={e => setField("name", e.target.value)} placeholder={t("verbindung.namePlatzhalter")}/>
                </div>
                <div className="iio-field">
                  <label className="iio-field-label" htmlFor="iio-inst-desc">{t("verbindung.beschreibung")}</label>
                  <textarea id="iio-inst-desc" className="iio-textarea" value={form.description} onChange={e => setField("description", e.target.value)} placeholder={t("verbindung.beschreibungPlatzhalter")}/>
                  <div className="iio-field-hint">{t("verbindung.beschreibungHinweis")}</div>
                </div>
                <div className="iio-field">
                  <span className="iio-field-label">{t("verbindung.tags")}</span>
                  <div className="iio-fields-3">
                    {[0, 1, 2, 3].map(i => (
                      <input key={i} className="iio-input" value={(form.tags || [])[i] || ""} onChange={e => setTag(i, e.target.value)}
                        onBlur={e => {
                          // A stage name typed here belongs in the dropdown. On blur it moves
                          // there, so there are not two paths to the same result.
                          const stufe = TAG_ZU_STUFE[String(e.target.value || "").trim().toLowerCase()];
                          if (stufe) { setTag(i, ""); setField("stufe", stufe); }
                        }}
                        placeholder={[t("verbindung.tagPlatzhalter1"), t("verbindung.tagPlatzhalter2"), t("verbindung.tagPlatzhalter3"), t("verbindung.tagPlatzhalter4")][i]}/>
                    ))}
                  </div>
                  <div className="iio-field-hint">{t("verbindung.tagsHinweis")}</div>
                </div>
                <div className="iio-field">
                  <label className="iio-field-label" htmlFor="iio-inst-stufe">{t("verbindung.stufe")}</label>
                  <select id="iio-inst-stufe" className="iio-input" value={form.stufe || ""} onChange={e => setField("stufe", e.target.value)} style={{ maxWidth: 320 }}>
                    <option value="">{t("verbindung.stufeKeine")}</option>
                    {STAGES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                  </select>
                  <div className="iio-field-hint">
                    {t("verbindung.stufeHinweis")}
                    {form.stufe && cicdStatus?.spaces?.[space?.id]?.configured && (
                      <span style={{ display: "block", marginTop: 4 }}>
                        <Trans t={t} i18nKey="verbindung.stufeEnv" values={{ env: stageLabel(form.stufe) }} components={{ b: <b/> }}/>
                      </span>
                    )}
                  </div>
                </div>
                <div className="iio-field">
                  <span className="iio-field-label">{t("verbindung.auth")}</span>
                  <div className="iio-auth-toggle">
                    <button type="button" disabled={istGeerbt} className={form.authType === "oauth" ? "is-active" : ""} onClick={() => setField("authType", "oauth")}>{t("verbindung.authOauth")}</button>
                    <button type="button" disabled={istGeerbt} className={form.authType === "basic" ? "is-active" : ""} onClick={() => setField("authType", "basic")}>{t("verbindung.authBasic")}</button>
                  </div>
                </div>
                {istGeerbt && (
                  <div className="iio-field-hint" style={{ marginTop: -4, marginBottom: 8, padding: "8px 10px", borderLeft: "3px solid var(--iio-info)", background: "color-mix(in srgb, var(--iio-info) 5%, var(--iio-bg-1))", borderRadius: "var(--iio-r-sm)", color: "var(--iio-text-2)" }}>
                    {form.vorlageName
                      ? <Trans t={t} i18nKey="verbindung.geerbt" values={{ name: form.vorlageName }} components={{ b: <b/> }}/>
                      : t("verbindung.geerbtOhneName")}
                  </div>
                )}
                <div className="iio-field">
                  <label className="iio-field-label">{t("verbindung.tenantUrl")}</label>
                  <input className="iio-input iio-input--mono" value={form.tenantUrl} onChange={e => setField("tenantUrl", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt} placeholder={t("verbindung.tenantUrlPlatzhalter")}/>
                </div>
                {form.authType === "oauth" ? <>
                  <div className="iio-field">
                    <label className="iio-field-label">{t("verbindung.tokenUrl")}</label>
                    <input className="iio-input iio-input--mono" value={form.tokenUrl} onChange={e => setField("tokenUrl", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt} placeholder={t("verbindung.tokenUrlPlatzhalter")}/>
                  </div>
                  <div className="iio-fields-2">
                    <div className="iio-field">
                      <label className="iio-field-label">{t("verbindung.clientId")}</label>
                      <input className="iio-input iio-input--mono" value={form.clientId} onChange={e => setField("clientId", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt}/>
                    </div>
                    <div className="iio-field">
                      <label className="iio-field-label">{t("verbindung.clientSecret")}</label>
                      <input className="iio-input iio-input--mono" type="password" value={form.clientSecret} onChange={e => setField("clientSecret", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt} placeholder={editing ? t("verbindung.unveraendert") : ""}/>
                    </div>
                  </div>
                </> : <>
                  <div className="iio-fields-2">
                    <div className="iio-field">
                      <label className="iio-field-label">{t("verbindung.benutzername")}</label>
                      <input className="iio-input iio-input--mono" value={form.username} onChange={e => setField("username", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt}/>
                    </div>
                    <div className="iio-field">
                      <label className="iio-field-label">{t("verbindung.passwort")}</label>
                      <input className="iio-input iio-input--mono" type="password" value={form.password} onChange={e => setField("password", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt} placeholder={editing ? t("verbindung.unveraendert") : ""}/>
                    </div>
                  </div>
                </>}
                {editing && (
                  <div className="iio-field" style={{ marginTop: 16 }}>
                    <span className="iio-field-label">{t("verbindung.testTitel")}</span>
                    <button type="button" className="iio-btn iio-btn--secondary" disabled={testingInModal} onClick={async () => {
                      setTestingInModal(true);
                      try {
                        const r = await api(`/api/instances/${editing.id}/test`, { method: "POST" });
                        addToast(r.success ? t("meldung.verbindungOk") : (r.message || t("gemeinsam:zustand.fehler")), r.success ? "success" : "error");
                      } catch (e) { addToast(e.message, "error"); }
                      setTestingInModal(false);
                    }}>{testingInModal ? t("verbindung.testLaeuft") : t("verbindung.testJetzt")}</button>
                  </div>
                )}
              </>}

              {modalTab === "werke" && <>
                <div className="iio-field">
                  <div style={{ fontSize: 12, color: "var(--iio-text-2)", marginBottom: 8, lineHeight: 1.5 }}>
                    {t("werke.info")}
                  </div>
                  {(Array.isArray(form.werke) ? form.werke : []).map((w, idx) => {
                    const wObj = typeof w === "string" ? { name: w, kennung: w } : w;
                    return (
                      <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 120px 32px", gap: 8, marginBottom: 8, alignItems: "end" }}>
                        <div className="iio-field" style={{ margin: 0 }}>
                          {idx === 0 && <span className="iio-field-label">{t("werke.beschreibung")}</span>}
                          <input className="iio-input" value={wObj.name || ""} onChange={e => {
                            const next = [...form.werke];
                            const cur = typeof next[idx] === "string" ? { name: next[idx], kennung: next[idx] } : { ...next[idx] };
                            cur.name = e.target.value;
                            next[idx] = cur;
                            setField("werke", next);
                          }} placeholder={t("werke.namePlatzhalter")}/>
                        </div>
                        <div className="iio-field" style={{ margin: 0 }}>
                          {idx === 0 && <span className="iio-field-label">{t("werke.bezeichner")}</span>}
                          <input className="iio-input" value={wObj.kennung || ""} onChange={e => {
                            const next = [...form.werke];
                            const cur = typeof next[idx] === "string" ? { name: next[idx], kennung: next[idx] } : { ...next[idx] };
                            cur.kennung = e.target.value;
                            next[idx] = cur;
                            setField("werke", next);
                          }} placeholder={t("werke.kennungPlatzhalter")}/>
                        </div>
                        <button type="button" onClick={() => {
                          setField("werke", form.werke.filter((_, i) => i !== idx));
                        }} title={t("werke.entfernen")} style={{
                          background: "none", border: "1px solid var(--iio-border)", borderRadius: "var(--iio-r-sm)",
                          color: "var(--iio-critical)", cursor: "pointer", height: 34, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
                        }}>x</button>
                      </div>
                    );
                  })}
                  <button type="button" onClick={() => setField("werke", [...(form.werke || []), { name: "", kennung: "" }])} style={{
                    background: "none", border: "1px dashed var(--iio-border)", borderRadius: "var(--iio-r-sm)",
                    padding: "6px 14px", fontSize: 12, color: "var(--iio-text-2)", cursor: "pointer", fontFamily: "var(--iio-font-sans)", marginTop: 2,
                  }}>{t("werke.hinzufuegen")}</button>
                  <div className="iio-field-hint" style={{ marginTop: 8 }}>{t("werke.hinweis")}</div>
                </div>
              </>}

              {/* On save, credentials go into a GitHub repository. That needs to be
                  announced before it happens. Only visible when a transfer actually
                  happens: space bound AND instance has a stage tag. */}
              {cicdStatus?.spaces?.[space?.id]?.configured && form.stufe && (
                <div className="iio-field-hint" style={{ marginTop: 14, lineHeight: 1.5 }}>
                  <Trans t={t} i18nKey="dialog.cicdHinweis" values={{ env: stageLabel(form.stufe) }} components={{ b: <b/> }}/>
                </div>
              )}
            </div>

            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left">
                {editing && delConfirm === editing.name ? (
                  <button className="iio-btn iio-btn--danger" onClick={submitDelete}>{t("dialog.loeschenEndgueltig")}</button>
                ) : editing ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input className="iio-input" style={{ width: 200, height: 32 }} value={delConfirm} onChange={e => setDelConfirm(e.target.value)} placeholder={t("dialog.loeschenPlatzhalter", { name: editing.name })}/>
                  </div>
                ) : null}
              </div>
              <div className="iio-modal-foot-right">
                <button className="iio-btn iio-btn--secondary" onClick={closeModal}>{t("gemeinsam:aktion.abbrechen")}</button>
                <button className="iio-btn iio-btn--signal" onClick={submitForm} disabled={submitting || !form.name.trim() || !form.tenantUrl.trim()}>
                  {submitting
                    ? (cicdStatus?.spaces?.[space?.id]?.configured && form.stufe
                        ? t("dialog.speichertUndUebertraegt")
                        : t("dialog.speichert"))
                    : editing ? t("gemeinsam:aktion.speichern") : t("dialog.instanzAnlegen")}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default SpaceDetail;
